import { ComplexValue } from 'app/types-shared';
import { Filter, CustomFilterType, PredefinedIds, FilterType, FilterCommonValues, PredefinedFilter,
  PredefinedFilterType } from 'app/area/search-filters/filters/types';
import { allPredefinedFilters, filterId, fieldType } from 'app/area/search-filters/constants';
import { queryFilters, SearchTranslation } from '../constants';
import { endpoint } from '../endpoints';
import { getFieldId } from 'app/area/search-filters/filters/selectors';
import { UserFiltersRenderConditions } from 'app/area/administration/list/users/types';

export const userQueryFilters = (translation: SearchTranslation, enableAlternativeId: boolean): ComplexValue<string>[] => [
  { value: queryFilters.name, description: translation.name },
  { value: queryFilters.email, description: translation.email },
  { value: queryFilters.id, description: translation.user_id },
  ...(enableAlternativeId ? [
    { value: queryFilters.alternativeId, description: translation.alternative_id },
    { value: queryFilters.alternativeName, description: translation.alternative_id_name }
  ] : [])
];

export const predefinedFilters = (translation: SearchTranslation, enableAlternativeId: boolean): PredefinedFilter => ({
  type: PredefinedFilterType.includeExclude,
  values: [
    { value: allPredefinedFilters.inactiveUsers, description: translation.inactive_users },
    { value: allPredefinedFilters.technicalUsers, description: translation.technical_users },
    { value: allPredefinedFilters.administratedByMe, description: translation.users_administrated_by_me },
    { value: allPredefinedFilters.withoutEmailAddress, description: translation.without_email },
    ...(enableAlternativeId ? [
      { value: allPredefinedFilters.withoutAlternativeID, description: translation.without_alternative_id }
    ] : [])
  ]
});

// eslint:disable-next-line:max-func-body-length
export const userFilters = (
  { hasExperienceTransferAlertsAccess, hasRestrictedAccess, hasRoles, readOnlyDefaultFilterPickers }: UserFiltersRenderConditions,
  translation: SearchTranslation
): Filter[] => [
  {
    id: filterId.userCode,
    label: translation.code,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.inputIntegerFilter,
        includeInSummary: true
      }
    ],
    type: FilterType.TEXT_RANGE
  },
  {
    id: filterId.createdDate,
    label: translation.created_date,
    fields: [
      {
        fieldTypeId: fieldType.date,
        type: CustomFilterType.dateRange,
        includeInSummary: true
      }
    ],
    type: FilterType.DATE_RANGE,
    options: {
      dateToAsEndOfDay: true
    }
  },
  {
    id: filterId.disabledDate,
    label: translation.disabled_date,
    fields: [
      {
        fieldTypeId: fieldType.date,
        type: CustomFilterType.dateRange,
        includeInSummary: true
      }
    ],
    type: FilterType.DATE_RANGE
  },
  {
    id: filterId.latestActivity,
    label: translation.latest_activity,
    fields: [
      {
        fieldTypeId: fieldType.date,
        type: CustomFilterType.dateRange,
        includeInSummary: true
      }
    ],
    type: FilterType.DATE_RANGE,
    options: {
      dateToAsEndOfDay: true
    }
  },
  {
    id: filterId.userType,
    label: translation.user_type,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.code,
        optionsEndpoint: endpoint.userType
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.mainUserGroup,
    label: translation.main_user_group,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.codeHierarchy,
        optionsEndpoint: endpoint.userGroup
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.currentUserGroup,
    label: translation.current_user_group,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.codeHierarchy,
        optionsEndpoint: endpoint.userGroup
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.myUnit,
    label: translation.unit,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.codeHierarchy,
        optionsEndpoint: endpoint.unit,
        optionsParams: { displayNotSelected: true }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.myLocation,
    label: translation.location,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.codeHierarchy,
        optionsEndpoint: endpoint.location,
        optionsParams: { displayNotSelected: true }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  hasRoles && {
    id: filterId.userRole,
    label: translation.role,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.code,
        optionsEndpoint: endpoint.userRole,
        optionsParams: { displayNotSelected: true }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.accessToOperation,
    label: translation.access_to_operation,
    fields: [
      {
        fieldTypeId: fieldType.application,
        label: translation.application_or_case_type,
        type: CustomFilterType.codeHierarchyParents,
        optionsEndpoint: endpoint.userApplication
      },
      {
        fieldTypeId: fieldType.root,
        label: translation.operation,
        type: CustomFilterType.code,
        optionsEndpoint: endpoint.userAccessToOperation,
        optionsParams: { displayNotSelected: true },
        divider: hasRoles
      },
      hasRoles && {
        fieldTypeId: fieldType.excludeInherited,
        type: CustomFilterType.checkbox,
        includeInSummary: false,
        options: [{ value: 1, title: translation.exclude_inherited_from_role }]
      }
    ].filter(field => !!field),
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.accessToScreen,
    label: translation.access_to_screen,
    fields: [
      {
        fieldTypeId: fieldType.application,
        label: translation.case_type,
        type: CustomFilterType.codeHierarchyParents,
        optionsEndpoint: endpoint.caseType,
        optionsParams: { includeSynergiLifeApplication: false }
      },
      {
        fieldTypeId: fieldType.root,
        label: translation.screen,
        type: CustomFilterType.code,
        optionsEndpoint: endpoint.accessToScreen,
        optionsParams: { displayNotSelected: true },
        divider: hasRoles
      },
      hasRoles && {
        fieldTypeId: fieldType.excludeInherited,
        type: CustomFilterType.checkbox,
        includeInSummary: false,
        options: [{ value: 1, title: translation.exclude_inherited_from_role }]
      }
    ].filter(field => !!field),
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.extendedAccess,
    label: translation.extended_access,
    fields: [
      {
        fieldTypeId: fieldType.application,
        label: translation.case_type,
        type: CustomFilterType.codeHierarchyParents,
        optionsEndpoint: endpoint.caseType,
        optionsParams: { includeSynergiLifeApplication: false }
      },
      {
        fieldTypeId: fieldType.root,
        label: translation.unit,
        type: CustomFilterType.codeHierarchy,
        optionsEndpoint: endpoint.responsibleForUnit,
        optionsParams: { displayNotSelected: true },
        divider: hasRoles
      },
      hasRoles && {
        fieldTypeId: fieldType.excludeInherited,
        type: CustomFilterType.checkbox,
        includeInSummary: false,
        options: [{ value: 1, title: translation.exclude_inherited_from_role }]
      }
    ].filter(field => !!field),
    type: FilterType.SELECTED_IDS
  },
  hasRestrictedAccess && {
    id: filterId.restrictedAccess,
    label: translation.restricted_access,
    fields: [
      {
        fieldTypeId: fieldType.application,
        label: translation.case_type,
        type: CustomFilterType.codeHierarchyParents,
        optionsEndpoint: endpoint.caseType,
        optionsParams: { includeSynergiLifeApplication: false }
      },
      {
        fieldTypeId: fieldType.root,
        label: translation.unit,
        type: CustomFilterType.codeHierarchy,
        optionsEndpoint: endpoint.responsibleForUnit,
        optionsParams: { displayNotSelected: true },
        divider: hasRoles
      },
      hasRoles && {
        fieldTypeId: fieldType.excludeInherited,
        type: CustomFilterType.checkbox,
        includeInSummary: false,
        options: [{ value: 1, title: translation.exclude_inherited_from_role }]
      }
    ].filter(field => !!field),
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.accessToUserGroup,
    label: translation.access_to_administrate_user_group,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.codeHierarchy,
        optionsEndpoint: endpoint.userGroup,
        optionsParams: { displayNotSelected: true }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.defaultValue,
    label: translation.default_values,
    fields: [
      {
        fieldTypeId: fieldType.codetable,
        label: translation.option_list,
        type: CustomFilterType.defaultValueSingleHierarchy,
        optionsEndpoint: endpoint.defaultValues.optionList,
        includeInSummary: true,
        defaultValueParams: {
          cleanValues: [
            getFieldId(filterId.defaultValue, fieldType.defaultValues),
            getFieldId(filterId.defaultValue, fieldType.domains)
          ]
        }
      },
      {
        fieldTypeId: fieldType.defaultValues,
        label: translation.default_value,
        type: CustomFilterType.defaultValueMultipleSubLevel,
        optionsEndpoint: endpoint.defaultValues.defaultValues,
        defaultValueParams: {
          readOnly: readOnlyDefaultFilterPickers,
          codetableId: getFieldId(filterId.defaultValue, fieldType.codetable)
        }
      },
      {
        fieldTypeId: fieldType.domains,
        label: translation.field,
        type: CustomFilterType.defaultValueMultiple,
        optionsEndpoint: endpoint.defaultValues.domains,
        optionsParams: { includeOptionsForAllUserTypes: true },
        defaultValueParams: {
          readOnly: readOnlyDefaultFilterPickers,
          codetableId: getFieldId(filterId.defaultValue, fieldType.codetable)
        }
      },
      {
        fieldTypeId: fieldType.application,
        label: translation.case_type,
        type: CustomFilterType.defaultValueSingleHierarchyParents,
        optionsEndpoint: endpoint.defaultValues.applications,
        defaultValueParams: {
          codetableId: getFieldId(filterId.defaultValue, fieldType.codetable)
        }
      },
      hasRoles && {
        fieldTypeId: fieldType.excludeInherited,
        type: CustomFilterType.checkbox,
        includeInSummary: false,
        options: [{ value: 1, title: translation.exclude_inherited_from_role }]
      }
    ].filter(field => !!field),
    type: FilterType.DEFAULT_VALUE
  },
  {
    id: filterId.fields,
    label: translation.visible_for_fields,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.codeWithoutExclude,
        optionsEndpoint: endpoint.visibleForFields,
        optionsParams: { displayNotSelected: true, levels: undefined }
      },
      hasRoles && {
        fieldTypeId: fieldType.excludeInherited,
        type: CustomFilterType.checkbox,
        includeInSummary: false,
        options: [{ value: 1, title: translation.exclude_inherited_from_role }]
      }
    ].filter(field => !!field),
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.responsibleForUnit,
    label: translation.responsible_for_unit,
    fields: [
      {
        fieldTypeId: fieldType.application,
        label: translation.case_type,
        type: CustomFilterType.codeHierarchyParents,
        optionsEndpoint: endpoint.caseType,
        optionsParams: { includeSynergiLifeApplication: false },
        divider: true
      },
      {
        fieldTypeId: fieldType.root,
        label: translation.unit,
        type: CustomFilterType.codeHierarchy,
        optionsEndpoint: endpoint.responsibleForUnit,
        optionsParams: { displayNotSelected: true }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  hasExperienceTransferAlertsAccess && {
    id: filterId.experienceTransferAlerts,
    label: translation.experience_transfer_alerts,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.code,
        optionsEndpoint: endpoint.experienceTransferAlerts,
        optionsParams: { displayNotSelected: true }
      }
    ],
    type: FilterType.SELECTED_IDS
  }
].filter(filter => !!filter);

export const defaultFilters = (translation: SearchTranslation): { [key: string]: FilterCommonValues<number>[] } => ({
  [PredefinedIds.exclude]: [
    { value: allPredefinedFilters.inactiveUsers, description: translation.inactive_users },
    { value: allPredefinedFilters.technicalUsers, description: translation.technical_users }
  ],
  [getFieldId(filterId.accessToOperation, fieldType.excludeInherited)]: [{
    value: 1, title: translation.exclude_inherited_from_role
  }],
  [getFieldId(filterId.extendedAccess, fieldType.excludeInherited)]: [{
    value: 1, title: translation.exclude_inherited_from_role
  }],
  [getFieldId(filterId.accessToScreen, fieldType.excludeInherited)]: [{
    value: 1, title: translation.exclude_inherited_from_role
  }],
  [getFieldId(filterId.restrictedAccess, fieldType.excludeInherited)]: [{
    value: 1, title: translation.exclude_inherited_from_role
  }],
  [getFieldId(filterId.fields, fieldType.excludeInherited)]: [{
    value: 1, title: translation.exclude_inherited_from_role
  }],
  [getFieldId(filterId.defaultValue, fieldType.excludeInherited)]: [{
    value: 1, title: translation.exclude_inherited_from_role
  }]
});
